import { AfterContentChecked, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Tools } from "@comunicare/comunicare-helper";
import {
  ACTION_STATUS_ENTITY,
  DrugInfo,
  EntityDrug,
  FORMS_MODE,
  ICycleSchema,
  IDrugInfo,
  IEntitylink,
  IPatientUser,
  IStepwiseDrug,
  KeyValue,
  PARENT_TYPE,
  STATUS_ENTITY,
} from "@comunicare/comunicare-interfaces";
import { TimingEditorComponent } from "@comunicare/comunicare-timing-editor";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FormsData } from "src/app/helpers/formsData";
import { Entitylink } from "src/app/models/entity.interface";
import { DrugsService } from "src/app/providers/drugs.service";
import { LanguagesService } from "src/app/providers/languages.service";
import { SessionService } from "src/app/providers/session.service";
import { UserStatisticsService } from "src/app/providers/userStatistics.service";
import { DrugCommentComponent } from "./drug-comment/drug-comment.component";
import { DrugCycleComponent } from "./drug-cycle/drug-cycle.component";
import { DrugServerSideSearchComponent } from "./drug-server-side-search-component/drug-server-side-search.component";

@Component({
  selector: "app-drug-add",
  templateUrl: "./drug-add.component.html",
  styleUrls: ["./drug-add.component.scss"],
})
export class DrugAddComponent implements OnInit, AfterContentChecked, OnDestroy {
  @ViewChild(DrugServerSideSearchComponent)
  search: DrugServerSideSearchComponent;
  @ViewChild(TimingEditorComponent) timing: TimingEditorComponent;
  @ViewChild(DrugCycleComponent) cycle: DrugCycleComponent;
  @ViewChild(DrugCommentComponent) comment: DrugCommentComponent;
  public availablePrescriptors: KeyValue[] = this.formsData.PRESCIPTORS;
  public drug: IEntitylink;
  public hasValidate = false;
  public drugForm = this.fb.group({
    allowUserEdit: [false, Validators.required],
    name: [undefined, Validators.required],
    prescriptor: [this.availablePrescriptors[0], Validators.required],
    authorization: this.fb.group({
      authorizationRequired: false,
      reimbursementAuthorizationEndDate: "",
    }),
  });
  public isLoading = true;
  public notUpdateName = true;
  public drugName = "";
  public drugPrescriptionName = "";
  public administratorTranslation: string;
  public hasCycle = false;
  public hasStepwise = false;
  public isInputValidate = false;
  public quantitiesMustBeNumber = false;
  public PARENT_TYPE = PARENT_TYPE;
  public authorizationReminderPeriods = [
    {
      period: 7,
      periodUnits: "d",
    },
    {
      period: 15,
      periodUnits: "d",
    },
    {
      period: 30,
      periodUnits: "d",
    },
  ];
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<DrugAddComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      patientUser: IPatientUser;
      drug: IEntitylink;
      mode: FORMS_MODE;
    },
    private fb: UntypedFormBuilder,
    private formsData: FormsData,
    private sessionService: SessionService,
    private drugService: DrugsService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private cdref: ChangeDetectorRef,
    private statService: UserStatisticsService,
    private languageService: LanguagesService
  ) {}

  ngOnInit(): void {
    this.initDrug();
    this.drugForm
      .get("name")
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((v: IDrugInfo) => {
        this.drugName = v?.[this.sessionService.userLang]?.fullName;
        this.drugPrescriptionName = v?.[this.sessionService.userLang]?.prescriptionNameFamhp;
      });
    this.isLoading = false;
  }

  ngAfterContentChecked(): void {
    this.cdref.detectChanges();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public initDrug(): void {
    if (this.data.mode === FORMS_MODE.CREATE) {
      const idPatient = this.data?.patientUser?.user?.caremateIdentifier;
      const idPractitioner = this.sessionService.account.caremateIdentifier;
      this.drug = Entitylink.createDrug(idPatient, idPractitioner, PARENT_TYPE.DRUG);
    } else if (this.data.mode === FORMS_MODE.UPDATE) {
      this.drug = this.data.drug;
      const drugData = this.drug.entityData as EntityDrug;
      this.hasCycle = drugData.cycle ? true : false;
      this.hasStepwise = drugData.stepwiseSchema ? true : false;
      this.drugName = drugData.name;
      this.drugPrescriptionName = drugData.prescriptionName;
      const prescriptor = this.availablePrescriptors.find((v) => v.value === drugData.performer.reference);
      const lockedForUser = this.drug.entityStatus?.includes(STATUS_ENTITY.LOCKED) ? true : false;
      this.drugForm = this.fb.group({
        allowUserEdit: [
          {
            value: !lockedForUser,
            disabled: this.drug.parentType === PARENT_TYPE.ACTIVITY_DRUG,
          },
          Validators.required,
        ],
        name: [this.drugInfo(drugData), Validators.required],
        prescriptor: [prescriptor, Validators.required],
        comment: [drugData.comment],
        authorization: this.fb.group({
          authorizationRequired: drugData.authorization?.authorizationRequired ?? false,
          reimbursementAuthorizationEndDate:
            drugData.authorization?.reimbursementAuthorizationEndDate ?? this.drug.entityData.frequency.boundsPeriod.end ?? "",
        }),
      });
      if (this.drug?.entityData?.administrator?.display) {
        if (this.drug.entityData.administrator.display.startsWith("$TR$")) {
          const term = this.drug.entityData.administrator.display.substring(4);
          const currentLang = this.translateService.currentLang || this.sessionService.DEFAULT_LANG;
          this.languageService.getTranslations(term).then((translations) => {
            this.administratorTranslation = translations[0]?.[currentLang] ?? this.drug.entityData.administrator.display;
          });
        } else {
          this.administratorTranslation = this.drug.entityData.administrator.display;
        }
      }
    }
    this.quantitiesMustBeNumber = !!this.drug.entityData.managedStock;
  }

  private drugInfo(drug: EntityDrug): IDrugInfo {
    const source = drug.source;
    let country: string;
    let dbName: string;
    if (Tools.isDefined(source)) {
      const split = source.split("/");
      country = split[0];
      dbName = split[1];
    }
    return DrugInfo.createDrugInfo(
      drug.name,
      dbName,
      country,
      drug.atcCode,
      drug.lastPackNumber,
      drug.vmpCode,
      drug.allPack,
      drug.knownPackaging,
      drug.prescriptionName
    );
  }

  public get isCreation(): boolean {
    return this.data.mode === FORMS_MODE.CREATE;
  }

  public drugChanged(newDrug: IDrugInfo): void {
    if (this.cycle) {
      this.cycle.drugChanged(newDrug);
    }
  }

  public cycleChanged(cycle: ICycleSchema): void {
    if (cycle) {
      this.hasCycle = true;
    } else {
      this.hasCycle = false;
    }
  }

  public stepwiseDrugChanged(stepwise: IStepwiseDrug): void {
    this.hasStepwise = stepwise ? true : false;
  }

  public save(close: boolean): void {
    this.hasValidate = true;
    const frequencyValid = this.timing.save();
    const cycleValid = this.cycle ? this.cycle.save() : true;
    const searchValid =
      this.isCreation && this.drug.parentType !== PARENT_TYPE.ACTIVITY_DRUG ? this.search.drugServerSideFilteringCtrl.valid : true;
    this.comment.save();
    const formIsValid = this.drugForm.valid && frequencyValid && cycleValid && searchValid;
    if (!formIsValid) {
      // mark child component formControl as touched in order to show error state
      // using ? because the drug search bar isn't always visible
      this.search?.drugServerSideFilteringCtrl?.markAsTouched();
      return;
    }
    const entityDrug = this.drug.entityData as EntityDrug;
    const drugInfo = this.drugForm.get("name").value as IDrugInfo;
    entityDrug.name = drugInfo?.[this.sessionService.userLang]?.fullName;
    entityDrug.prescriptionName = drugInfo?.[this.sessionService.userLang]?.prescriptionNameFamhp;

    entityDrug.reference = drugInfo?.cnk;
    entityDrug.source =
      Tools.isDefined(drugInfo?.country) && Tools.isDefined(drugInfo?.dbName) ? `${drugInfo?.country}/${drugInfo?.dbName}` : null;
    // we authorise the management of stocks of medicines that do not come from an official source:
    entityDrug.knownPackaging = entityDrug.source ? drugInfo?.knownPackaging : true;
    entityDrug.allPack = entityDrug.source && drugInfo?.allPack ? drugInfo?.allPack : [];
    entityDrug.lastPackNumber = entityDrug.source ? drugInfo?.packNumber : -1;
    entityDrug.vmpCode = drugInfo?.vmpCode;
    entityDrug.atcCode = drugInfo?.atc?.code;

    const prescriptor = this.drugForm.get("prescriptor").value as KeyValue;
    entityDrug.performer = {
      display: prescriptor.key,
      reference: prescriptor.value,
    };
    this.drug.actionStatus = ACTION_STATUS_ENTITY.CREATED;
    if (!this.drug.entityStatus) {
      this.drug.entityStatus = [];
    }
    const i = this.drug.entityStatus.findIndex((s) => s === STATUS_ENTITY.LOCKED);
    if (this.drugForm.get("allowUserEdit").value && i > -1) {
      this.drug.entityStatus.splice(i, 1);
    } else if (!this.drugForm.get("allowUserEdit").value && i === -1) {
      this.drug.entityStatus.push(STATUS_ENTITY.LOCKED);
    }
    if (entityDrug.cycle) {
      entityDrug.cycle.startDate = new Date(entityDrug.frequency.boundsPeriod.start);
    }

    const authorizationForm = this.drugForm.get("authorization").getRawValue();
    if (authorizationForm?.authorizationRequired) {
      if (entityDrug.authorization) {
        entityDrug.authorization.authorizationRequired = true;
        entityDrug.authorization.reimbursementAuthorizationEndDate = moment(authorizationForm.reimbursementAuthorizationEndDate).format();
      } else {
        authorizationForm.reimbursementAuthorizationEndDate = moment(authorizationForm.reimbursementAuthorizationEndDate).format();
        entityDrug.authorization = authorizationForm;
      }
    } else {
      entityDrug.authorization = undefined;
    }
    this.drugService
      .save(this.drug)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.snackBar.open(this.translateService.instant("common.success"), "ok", { duration: 3000 });
        this.sessionService.needRefreshDrugsData();
        this.statService.createStatEvent("Drug with stepwise schema created : " + this.drug.entityData.name);
        if (close) {
          this.dialogRef.close();
        } else {
          this.hasValidate = false;
          this.cleanForm(prescriptor);
          this.initDrug();
        }
      });
  }

  private cleanForm(prescrptior?: KeyValue): void {
    this.drugForm = this.fb.group({
      allowUserEdit: [false, Validators.required],
      name: [undefined, Validators.required],
      prescriptor: [prescrptior ? prescrptior : this.availablePrescriptors[0], Validators.required],
      comment: [],
      authorization: this.fb.group({
        authorizationRequired: false,
        reimbursementAuthorizationEndDate: this.drug.entityData.frequency.boundsPeriod.end ?? "",
      }),
    });
    this.search?.drugServerSideFilteringCtrl.setValue(undefined);
  }

  public get authorizationRequired(): boolean {
    return this.drugForm.get("authorization").get("authorizationRequired").value;
  }

  public onAuthorizationRequiredChange(): void {
    this.timing.updateFreqFromForm();
    this.drugForm
      .get("authorization")
      .get("reimbursementAuthorizationEndDate")
      .setValue(this.drug.entityData.frequency.boundsPeriod.end ?? "");
  }

  public onAuthorizationReminderChange(period: { period: number; periodUnits: string }): void {
    const index = this.drug.entityData.authorization?.notificationsSettings?.reminderPeriods?.findIndex(
      (p) => p.period === period.period && p.periodUnits === period.periodUnits
    );
    if (Tools.isDefined(index) && index !== -1) {
      this.drug.entityData.authorization.notificationsSettings?.reminderPeriods.splice(index, 1);
    } else {
      if (this.drug.entityData.authorization?.notificationsSettings) {
        this.drug.entityData.authorization.notificationsSettings?.reminderPeriods.push(period);
      } else {
        this.drug.entityData.authorization = {
          notificationsSettings: {
            reminderPeriods: [period],
          },
        };
      }
    }
  }
}
